import React, { useMemo } from "react";
import { Box, Grid, Paper, Typography, useTheme, Skeleton, Theme, SxProps, alpha, useMediaQuery } from "@mui/material";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { getStatusColor } from "../../utils/Utility";
import { useSelector } from "react-redux";
import { selectIsValueFoundInToken } from "../../redux/slice/authSlice";
import { RootState } from "../../redux/store";
import { CAN_ACCESS_KPI_CONSTANTS } from "../../constants/applicationConstants";
import { tokens } from "../../theme";
import { useDashboardReducer } from "../../redux/slice/dashboard/dashboardSlice";
import NoDataCircle from "./NoDataCircle";

/** ----------------------------------------------------------------
 *  Labels
 * ---------------------------------------------------------------- */
const ivrLabels = [
  "Canceled",
  "Denied",
  "Drafted",
  "Needs More Info",
  "Submitted",
  "Verified",
  "Pre-Auth",
] as const;

const orderLabels = [
  "Placed",
  "Shipped",
  "Delivered",
  "Canceled",
  "Processing",
  "Pending",
  "Delayed",
] as const;

/** ----------------------------------------------------------------
 *  Loading Placeholder (donut skeleton)
 * ---------------------------------------------------------------- */
const LoadingDonutPlaceholder: React.FC<{ height?: number }> = ({ height = 150 }) => (
  <Box
    sx={{
      height,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 3,
      px: 2,
    }}
  >
    {/* donut ring */}
    <Skeleton
      variant="circular"
      width={80}
      height={150}
      sx={{
        position: "relative",
        animationDuration: "1.2s",
        "::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          // fake donut hole
          mask: "radial-gradient(circle at center, transparent 58%, black 59%)",
          background:
            "linear-gradient(90deg, rgba(255,255,255,.08), rgba(255,255,255,.22), rgba(255,255,255,.08))",
          animation: "sweep 1.6s linear infinite",
        },
        "@keyframes sweep": {
          "0%": { transform: "translateX(-100%)" },
          "100%": { transform: "translateX(100%)" },
        },
      }}
    />
    {/* legend rows */}
    <Box sx={{ minWidth: 180, width: "40%", maxWidth: 280 }}>
      {[...Array(6)].map((_, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", mb: 1.25, gap: 1 }}>
          <Skeleton variant="text" width={`${70 - i * 5}%`} height={16} />
        </Box>
      ))}
    </Box>
  </Box>
);

/** ----------------------------------------------------------------
 *  Base donut options (shared)
 * ---------------------------------------------------------------- */
const baseDonutOptions = (legendCount: number, compact: boolean = false): ApexOptions => ({
  chart: { type: "donut", background: "transparent" },
  legend: {
    position: "bottom",
    labels: { colors: Array(legendCount).fill("#e0f2f1") },
    fontSize: "9px",
    itemMargin: { horizontal: 2, vertical: 1 },
  },
  dataLabels: { enabled: false },
  tooltip: {
    theme: "dark",
    y: { formatter: (val: number) => val.toLocaleString() },
  },
  stroke: { width: 0 },
  plotOptions: {
    pie: {
      donut: {
        size: "70%",
        labels: {
          show: true,
          name: { 
            show: true, 
            offsetY: -10, 
            color: "#94a3b8", 
            fontSize: compact ? "8px" : "9px" 
          },
          value: {
            show: true,
            fontSize: compact ? "10px" : "16px",
            fontWeight: 600,
            color: "#ffffff",
            offsetY: -8,
            formatter: (val: string) => Number(val).toLocaleString(),
          },
          total: {
            show: true,
            label: "Total",
            fontSize: compact ? "7px" : "14px",
            color: "#94a3b8",
          },
        },
      },
    },
  },
});

/** ----------------------------------------------------------------
 *  Types
 * ---------------------------------------------------------------- */
type StatusBreakdownLoading = { ivr?: boolean; order?: boolean } | boolean;

const StatusBreakdown: React.FC<{ loading?: StatusBreakdownLoading }> = ({ loading }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { orderBreakdownStats, ivrBreakdownStats } = useDashboardReducer();
  const isCompact = useMediaQuery("(min-width:600px) and (max-width:1000px)");

  const canAccessIVRDonut = useSelector((state: RootState) =>
    selectIsValueFoundInToken("canAccessKPI", CAN_ACCESS_KPI_CONSTANTS.IVR_DONUT)(state)
  );
  const canAccessOrderDonut = useSelector((state: RootState) =>
    selectIsValueFoundInToken("canAccessKPI", CAN_ACCESS_KPI_CONSTANTS.ORDER_DONUT)(state)
  );

  /** --------------------------------------------------------------
   *  Color normalization (avoid 'inherit' or near-white on light bg)
   * -------------------------------------------------------------- */
  const normalizeWhite = (hex: string) => {
    const c = (hex || "").toLowerCase();
    return c === "inherit"
      ? theme.palette.mode === "dark"
        ? "#a0a0a0ff"
        : "#666666ff"
      : c || (theme.palette.mode === "dark" ? "#a0a0a0ff" : "#666666ff");
  };

  const ivrColors = useMemo(
    () => ivrLabels.map((lbl) => normalizeWhite(getStatusColor(lbl))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme.palette.mode]
  );
  const orderColors = useMemo(
    () => orderLabels.map((lbl) => normalizeWhite(getStatusColor(lbl))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme.palette.mode]
  );

  /** --------------------------------------------------------------
   *  Series
   * -------------------------------------------------------------- */
  const ivrData = useMemo<number[]>(
    () => [
      ivrBreakdownStats?.canceled ?? 0,
      ivrBreakdownStats?.denied ?? 0,
      ivrBreakdownStats?.drafted ?? 0,
      ivrBreakdownStats?.needMoreInfo ?? 0,
      ivrBreakdownStats?.submitted ?? 0,
      ivrBreakdownStats?.verified ?? 0,
      ivrBreakdownStats?.preAuth ?? 0,
    ],
    [ivrBreakdownStats]
  );

  const orderData = useMemo<number[]>(
    () => [
      orderBreakdownStats?.placed ?? 0,
      orderBreakdownStats?.shipped ?? 0,
      orderBreakdownStats?.delivered ?? 0,
      orderBreakdownStats?.canceled ?? 0,
      orderBreakdownStats?.processing ?? 0,
      orderBreakdownStats?.pending ?? 0,
      orderBreakdownStats?.delayed ?? 0,
    ],
    [orderBreakdownStats]
  );

  const hasAnyIvr = useMemo(() => ivrData.some((n) => n > 0), [ivrData]);
  const hasAnyOrder = useMemo(() => orderData.some((n) => n > 0), [orderData]);

  /** --------------------------------------------------------------
   *  Loading logic (force via prop or auto-detect)
   * -------------------------------------------------------------- */
  const forceIvr = typeof loading === "boolean" ? loading : loading?.ivr;
  const forceOrder = typeof loading === "boolean" ? loading : loading?.order;

  const autoIvrLoading =
    canAccessIVRDonut &&
    !hasAnyIvr &&
    !(typeof ivrBreakdownStats?.total === "number");
  const autoOrderLoading =
    canAccessOrderDonut &&
    !hasAnyOrder &&
    !(typeof orderBreakdownStats?.total === "number");

  const isIvrLoading = !!(forceIvr ?? autoIvrLoading);
  const isOrderLoading = !!(forceOrder ?? autoOrderLoading);

  /** --------------------------------------------------------------
   *  Totals + Options
   * -------------------------------------------------------------- */
  const ivrTotalFromSeries = useMemo(
    () => ivrData.reduce((a, b) => a + b, 0),
    [ivrData]
  );
  const orderTotalFromSeries = useMemo(
    () => orderData.reduce((a, b) => a + b, 0),
    [orderData]
  );

  const ivrOptions: ApexOptions = useMemo(() => {
    const opts = baseDonutOptions(ivrLabels.length, isCompact);
    return {
      ...opts,
      labels: [...ivrLabels],
      colors: ivrColors,
      legend: { ...opts.legend, show: false },
      plotOptions: {
        ...opts.plotOptions,
        pie: {
          ...opts.plotOptions?.pie,
          donut: {
            ...opts.plotOptions?.pie?.donut,
            labels: {
              ...opts.plotOptions?.pie?.donut?.labels,
              total: {
                ...opts.plotOptions?.pie?.donut?.labels?.total,
                formatter: () =>
                  (ivrBreakdownStats?.total ?? ivrTotalFromSeries).toLocaleString(),
              },
            },
          },
        },
      },
    };
  }, [ivrColors, orderBreakdownStats?.total, ivrTotalFromSeries]);

  const orderOptions: ApexOptions = useMemo(() => {
    const opts = baseDonutOptions(orderLabels.length, isCompact);
    return {
      ...opts,
      labels: [...orderLabels],
      colors: orderColors,
      legend: { ...opts.legend, show: false },
      plotOptions: {
        ...opts.plotOptions,
        pie: {
          ...opts.plotOptions?.pie,
          donut: {
            ...opts.plotOptions?.pie?.donut,
            labels: {
              ...opts.plotOptions?.pie?.donut?.labels,
              total: {
                ...opts.plotOptions?.pie?.donut?.labels?.total,
                formatter: () =>
                  (orderBreakdownStats?.total ?? orderTotalFromSeries).toLocaleString(),
              },
            },
          },
        },
      },
    };
  }, [orderColors, orderBreakdownStats?.total, orderTotalFromSeries]);


  return (
    <Box>
        <Grid container spacing={2} justifyContent="center">
          {canAccessIVRDonut && (
            <Grid item xs={12} md={canAccessOrderDonut ? 6 : 12}>
              <Typography variant="h6" sx={{ mb: 0 }}>
                IVRs
              </Typography>
              <Typography sx={{ color: "#94a3b8", mb: 2 }}>By Status</Typography>
                <Grid container spacing={2} justifyContent="center">
                  <Grid item xs={12} md={canAccessOrderDonut ? 12: 7}>
                      {isIvrLoading ? (
                        <LoadingDonutPlaceholder />
                      ) : hasAnyIvr ? (
                        <Box
                          sx={{
                            "& .apexcharts-datalabel-value": { fill: "#4caf50 !important" },
                            "& .apexcharts-datalabel-label": { fill: "#94a3b8 !important" },
                            "& .apexcharts-tooltip": {
                              color:
                                theme.palette.mode === "dark"
                                  ? "#000000 !important"
                                  : "#ffffff !important",
                              border: `1px solid ${
                                theme.palette.mode === "dark" ? "#374151" : "#e5e7eb"
                              } !important`,
                            },
                          }}
                        >
                          <Chart options={ivrOptions} series={ivrData} type="donut" height={300} />
                        </Box>
                      ) : (
                        <NoDataCircle label=" No IVR data available" thicknessPct={14} />
                      )}
                  </Grid>
                </Grid>
            </Grid>
          )}

          {canAccessOrderDonut && (
            <Grid item xs={12} md={canAccessIVRDonut ? 6 : 12}>
              <Typography variant="h6" sx={{ mb: 0 }}>
                Orders
              </Typography>
              <Typography sx={{ color: "#94a3b8", mb: 2 }}>By Status</Typography>

              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12} md={canAccessIVRDonut ? 12: 7}>
                  {isOrderLoading ? (
                    <LoadingDonutPlaceholder />
                  ) : hasAnyOrder ? (
                    <Box
                      sx={{
                        "& .apexcharts-datalabel-value": { fill: "#4caf50 !important" },
                        "& .apexcharts-datalabel-label": { fill: "#94a3b8 !important" },
                        "& .apexcharts-tooltip": {
                          color:
                            theme.palette.mode === "dark"
                              ? "#000000 !important"
                              : "#ffffff !important",
                          border: `1px solid ${
                            theme.palette.mode === "dark" ? "#374151" : "#e5e7eb"
                          } !important`,
                        },
                      }}
                    >
                      <Chart options={orderOptions} series={orderData} type="donut" height={300} />
                    </Box>
                  ) : (
                    <NoDataCircle label="No order data available" thicknessPct={14} />
                  )}
                </Grid>
              </Grid>

              
            </Grid>
          )}
        </Grid>
    </Box>
  );
};

export default StatusBreakdown;
